// uploadStatementToS3.jsx
// Dedicated upload function for receipt/statement uploads only
// Upload to S3 FIRST, then create database record
// Proper error handling with timeouts and retries

import { uploadData, getUrl } from 'aws-amplify/storage'; // Fixed: added getUrl here
import { fetchAuthSession } from 'aws-amplify/auth';
import { addFileToUploadedDocuments } from '../../graphqlCompnents/AWS/S3/addFileToUploadedDocuments';
import { v4 as uuidv4 } from 'uuid';
import PropTypes from 'prop-types';
import { 
  S3Client, 
  CreateMultipartUploadCommand, 
  UploadPartCommand, 
  CompleteMultipartUploadCommand,
  AbortMultipartUploadCommand 
} from '@aws-sdk/client-s3';

// Configuration for receipt uploads
// Industry Standards:

// AWS SDK default: 120 seconds (too long for mobile)
// Google Drive: ~30 seconds for photos
// Social media apps: 15-25 seconds
// Progressive Web Apps: 10-30 seconds
const UPLOAD_TIMEOUT = 30000; // 30 seconds - receipts are usually small
const MAX_RETRIES = 3;
const INITIAL_RETRY_DELAY = 2000; // 2 seconds
const MULTIPART_THRESHOLD = 5 * 1024 * 1024; // 5MB - use multipart for files larger than this

/**
 * Upload small files using Amplify's uploadData
 * For files under 5MB (most receipts)
 */
const uploadSmallFile = async (fileObj, bucket, fileKey, mimeType, timeout = UPLOAD_TIMEOUT) => {
  console.log('📤 [RECEIPT] Using standard upload for small file');

  const uploadParams = {
    path: fileKey,
    data: fileObj,
    options: {
      contentType: mimeType,
      bucket: {
        bucketName: bucket,
        region: 'us-east-1',
      },
    }
  };

  // Upload with timeout protection
  const uploadPromise = uploadData(uploadParams).result;

  const timeoutPromise = new Promise((_, reject) =>
    setTimeout(() => reject(new Error('Upload timeout')), timeout)
  );

  await Promise.race([uploadPromise, timeoutPromise]);

  console.log('✅ [RECEIPT] Small file upload completed');
};

/**
 * Upload large files using multipart upload
 * For files 5MB and larger (rare for receipts)
 */
const uploadLargeFile = async (file, bucket, key, timeout = UPLOAD_TIMEOUT) => {
  let uploadId = null;

  try {
    console.log('📤 [RECEIPT] Using multipart upload for large file');

    // Fetch credentials
    const session = await fetchAuthSession();
    const credentials = session.credentials;

    if (!credentials) {
      throw new Error('No credentials found. User may not be authenticated.');
    }

    // Configure S3 client
    const s3Client = new S3Client({
      region: 'us-east-1',
      credentials: {
        accessKeyId: credentials.accessKeyId,
        secretAccessKey: credentials.secretAccessKey,
        sessionToken: credentials.sessionToken,
      },
    });

    // Step 1: Initiate multipart upload
    const createUploadResponse = await s3Client.send(
      new CreateMultipartUploadCommand({
        Bucket: bucket,
        Key: key,
      })
    );
    uploadId = createUploadResponse.UploadId;
    console.log('📋 [RECEIPT] Multipart upload initiated:', uploadId);

    // Step 2: Upload parts with timeout protection
    const partSize = 5 * 1024 * 1024; // 5 MB per part
    const parts = [];
    let partNumber = 1;
    const startTime = Date.now();

    for (let start = 0; start < file.size; start += partSize) {
      // Check timeout
      if (Date.now() - startTime > timeout) {
        throw new Error('Multipart upload timeout');
      }

      const end = Math.min(start + partSize, file.size);
      const chunk = file.slice(start, end);

      console.log(`📦 [RECEIPT] Uploading part ${partNumber}/${Math.ceil(file.size / partSize)}`);

      const uploadPartResponse = await s3Client.send(
        new UploadPartCommand({
          Bucket: bucket,
          Key: key,
          PartNumber: partNumber,
          UploadId: uploadId,
          Body: chunk,
        })
      );

      parts.push({
        PartNumber: partNumber,
        ETag: uploadPartResponse.ETag,
      });

      partNumber++;
    }

    // Step 3: Complete multipart upload
    await s3Client.send(
      new CompleteMultipartUploadCommand({
        Bucket: bucket,
        Key: key,
        UploadId: uploadId,
        MultipartUpload: {
          Parts: parts,
        },
      })
    );

    console.log('✅ [RECEIPT] Multipart upload completed successfully');

  } catch (err) {
    // Abort multipart upload on error to avoid orphaned parts
    if (uploadId) {
      try {
        const session = await fetchAuthSession();
        const s3Client = new S3Client({
          region: 'us-east-1',
          credentials: session.credentials,
        });

        await s3Client.send(
          new AbortMultipartUploadCommand({
            Bucket: bucket,
            Key: key,
            UploadId: uploadId,
          })
        );
        console.log('🧹 [RECEIPT] Aborted incomplete multipart upload');
      } catch (abortError) {
        console.error('[RECEIPT] Failed to abort multipart upload:', abortError);
      }
    }

    throw new Error(`Multipart upload failed: ${err.message}`);
  }
};

/**
 * Retry wrapper with exponential backoff
 */
const retryUpload = async (uploadFunction, maxRetries = MAX_RETRIES, initialDelay = INITIAL_RETRY_DELAY) => {
  let lastError;

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      console.log(`🔄 [RECEIPT] Upload attempt ${attempt} of ${maxRetries}`);
      
      await uploadFunction();
      
      console.log(`✅ [RECEIPT] Upload successful on attempt ${attempt}`);
      return; // Success!
      
    } catch (error) {
      lastError = error;
      console.warn(`❌ [RECEIPT] Upload attempt ${attempt} failed:`, error.message);

      // Don't retry on authentication errors
      if (error.message.includes('credential') || 
          error.message.includes('authentication') ||
          error.message.includes('unauthorized')) {
        throw new Error('Authentication error. Please sign in again.');
      }

      // If this was the last attempt, throw the error
      if (attempt === maxRetries) {
        break;
      }

      // Calculate exponential backoff delay
      const delay = initialDelay * Math.pow(2, attempt - 1);
      console.log(`⏳ [RECEIPT] Retrying in ${delay}ms...`);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }

  // All retries failed
  throw new Error(`Upload failed after ${maxRetries} attempts: ${lastError.message}`);
};


export const uploadStatementToS3 = async (props) => {
  if (!props || !props.file) {
    throw new Error('No file provided for upload');
  }

  if (!props.bucket) {
    throw new Error('No bucket specified for upload');
  }

  console.log('=== [RECEIPT] Starting S3 Upload ===');

  try {
    // Get authentication session
    const session = await fetchAuthSession();
    const identityId = session?.identityId;

    if (!identityId) {
      throw new Error('Unable to get identity ID. Please sign in again.');
    }

    // Prepare file information
    const fileObj = props.file;
    const filename = props.filename || props.file.name;
    const extension = filename.split('.').pop().toLowerCase();
    const mimeType = props.filetype || props.file.type;
    const bucket = props.bucket;
    const fileSize = fileObj.size;

    // Validate file type (receipts should be images or PDFs)
    const validExtensions = ['jpg', 'jpeg', 'png', 'pdf', 'gif'];
    if (!validExtensions.includes(extension)) {
      throw new Error(`Invalid file type. Please upload images (JPG, PNG) or PDF files only.`);
    }

    // Validate file size (max 20MB for receipts)
    const maxSize = 50 * 1024 * 1024; // 50MB
    if (fileSize > maxSize) {
      throw new Error(`File too large. Please upload files smaller than 50MB.`);
    }

    // Generate unique file key
    const encodedIdentityId = decodeURIComponent(identityId);
    const fileKey = `protected/${encodedIdentityId}/${uuidv4()}.${extension}`;

    console.log('📄 [RECEIPT] File Info:', {
      filename,
      size: `${(fileSize / 1024 / 1024).toFixed(2)} MB`,
      type: mimeType,
      extension,
      key: fileKey,
    });

    // ===== STEP 1: UPLOAD TO S3 FIRST =====
    console.log('📤 [RECEIPT] Step 1: Uploading to S3...');

    const uploadFunction = fileSize >= MULTIPART_THRESHOLD
      ? () => uploadLargeFile(fileObj, bucket, fileKey)
      : () => uploadSmallFile(fileObj, bucket, fileKey, mimeType);

    // Upload with retry logic
    await retryUpload(uploadFunction);

    console.log('✅ [RECEIPT] S3 upload completed successfully');

    // ===== STEP 2: CREATE DATABASE RECORD =====
    console.log('💾 [RECEIPT] Step 2: Creating database record...');

    const fileParams = {
      bucket: bucket,
      identityId: identityId,
      key: fileKey,
    };

    let uploadedDocumentId;
    try {
      uploadedDocumentId = await addFileToUploadedDocuments(fileParams);
      console.log('✅ [RECEIPT] Database record created:', uploadedDocumentId);
    } catch (dbError) {
      console.error('❌ [RECEIPT] Database record creation failed:', dbError);
      throw new Error(`Receipt uploaded to S3 but database record creation failed: ${dbError.message}`);
    }

    // ===== STEP 3: GENERATE SIGNED URL =====
    console.log('🔗 [RECEIPT] Step 3: Generating signed URL...');

    let signedUrl = '';
    try {
      // Generate a signed URL that expires in 7 days (604800 seconds)
      const urlResult = await getUrl({
        path: {
          bucket,     // e.g. "analyze-expense-statements-dev"
          key: fileKey, // "protected/us-east-1:identity/...png"
        },
        options: {
          expiresIn: 604800,
        },
      });

      signedUrl = urlResult.url.href;
      console.log('✅ [RECEIPT] Signed URL generated');
    } catch (urlError) {
      console.warn('⚠️ [RECEIPT] Could not generate signed URL:', urlError.message);
      // Fallback to a direct URL pattern
      signedUrl = `https://${bucket}.s3.amazonaws.com/${fileKey}`;
    }

    // ===== STEP 4: RETURN SUCCESS DATA =====
    const returnData = {
      fileName: fileKey,
      identityId: identityId,
      key: fileKey,
      documentId: uploadedDocumentId,
      url: signedUrl, // CRITICAL: Add the URL property
      bucket: bucket,
      size: fileSize,
      filetype: mimeType
    };

    console.log('🎉 [RECEIPT] Upload process completed successfully');
    return returnData;

  } catch (error) {
    console.error('❌ [RECEIPT] Upload failed:', error);

    // Provide user-friendly error messages
    if (error.message.includes('timeout') || error.message.includes('Upload timeout')) {
      throw new Error('Receipt upload timed out. Please check your network connection and try again.');
    } else if (error.message.includes('network') || error.message.toLowerCase().includes('network')) {
      throw new Error('Network error during upload. Please check your internet connection and try again.');
    } else if (error.message.includes('sign in') || error.message.includes('authentication')) {
      throw error; // Already has good message
    } else if (error.message.includes('database record creation failed')) {
      throw error; // Already has good message
    } else if (error.message.includes('Invalid file type') || error.message.includes('File too large')) {
      throw error; // Already has good message
    } else {
      throw new Error(`Receipt upload failed: ${error.message}`);
    }
  }
};

uploadStatementToS3.propTypes = {
  file: PropTypes.object.isRequired,
  filename: PropTypes.string,
  filetype: PropTypes.string,
  bucket: PropTypes.string.isRequired,
};

// Export as default as well for convenience
export default uploadStatementToS3;